import { CustomTooltipContentProps } from '../../../../../common';
import { DefaultEventCategoryId, EventCategorySet } from '@demind-inc/core';
import {
  DataTFunction,
  getDefaultEventCategoryDescription,
  getEventCategoryEnergyTooltipTitle,
} from '../../../../i18n';

export const getEnergyFluctationTooltipContent = (
  t: DataTFunction,
  reason: string,
  category?: EventCategorySet
): CustomTooltipContentProps | undefined => {
  if (reason === 'sleep') {
    return {
      title: t('data.circadian.tooltips.sleepAdjustmentTitle'),
      description: t('data.circadian.tooltips.sleepAdjustmentDescription'),
      referenceLinkList: [
        {
          title:
            'Boredom effects on sleepiness/alertness in the early afternoon vs. early evening and interactions with warm ambient temperature',
          url: 'https://pubmed.ncbi.nlm.nih.gov/7921744/',
        },
      ],
    };
  } else if (reason === 'phaseEma') {
    return {
      title: t('data.circadian.tooltips.subjectiveEnergyAdjustmentTitle'),
      description: t('data.circadian.tooltips.subjectiveEnergyAdjustmentDescription'),
    };
  } else {
    if (category) {
      const fallback =
        defaultEventCategoryExplaSet[category.id as DefaultEventCategoryId];
      const description = getDefaultEventCategoryDescription(
        t,
        category.id as DefaultEventCategoryId,
        fallback?.description ?? ''
      );

      return {
        title: getEventCategoryEnergyTooltipTitle(t, category),
        description: `${description}\n${t('data.circadian.tooltips.eventAdjustmentDescription')}`,
        referenceLinkList: [
          {
            title: 'Mood Prediction Based on Calendar Events Using Multitask Learning',
            url: 'https://ieeexplore.ieee.org/document/9839466',
          },
        ],
      };
    }
    return;
  }
};

export const defaultEventCategoryExplaSet: Record<
  DefaultEventCategoryId,
  { title: string; description: string }
> = {
  default_exercise: {
    title: 'Exercise',
    description: 'Exercise may use energy now, but often increases energy later.',
  },
  default_break: {
    title: 'Break',
    description: 'Breaks help restore mental and physical energy. Short breaks can lift your day.',
  },
  default_meal: {
    title: 'Meal',
    description:
      'Meals or snacks can cause a short dip, then support steadier energy for the rest of the day.',
  },
  default_deepWork: {
    title: 'Deep-Work',
    description:
      'Deep work is demanding. Share how you felt afterward to improve future energy predictions.',
  },
  default_lightTask: {
    title: 'Light-Task',
    description: 'Light tasks (like admin work) usually have a smaller energy impact.',
  },
  default_mtg: {
    title: 'Meeting',
    description:
      'Meetings can drain or energize you. Your feedback matters more than the category alone.',
  },
  default_other: {
    title: '',
    description:
      'Report how energized you feel for short-term adjustments. Categorize events for better predictions.',
  },
  default_social: {
    title: 'Social',
    description:
      'Social events affect people differently. Tell us how you felt to improve predictions.',
  },
};
